import { ButtonGroup, FormControl, MenuItem, Select } from '@material-ui/core';
import cx from 'classnames';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import MultidiePlate from 'components/common/multidie/MultidiePlate';
import TeethContextMenu from 'components/common/teeth/TeethContextMenu';
import TeethSvgV2 from 'components/common/teeth/TeethSvgV2';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import { IToothShade, Tooth } from 'lib/types/projectTypes';
import React, { useMemo } from 'react';
import styled from 'styled-components';

interface TeethViewerProps {
  isMultidie?: number;
  isViewer: boolean;
  numbering: { value: number; onChange: (e: any) => void; setValue: (value: number) => void };
  teeth: {
    value: object[] | any;
    onChange: (e: any) => void;
    setValue: (value: object[] | any) => void;
  };
  tooth: {
    value: Tooth;
    onChange: (e: any) => void;
    setValue: (value: Tooth) => void;
  };
  bridge: { value: string[]; onChange: (e: any) => void; setValue: (value: string[]) => void };
  copyData?: {
    value: object | null | any;
    onChange: (e: any) => void;
    setValue: (value: object) => void;
  };
  toothSvgRef?: HTMLDivElement | any;
  teethContextActions?: {
    value: {
      copy: {
        active: boolean;
        hidden: boolean;
      };
      paste: {
        active: boolean;
        hidden: boolean;
      };
      delete: {
        active: boolean;
        hidden: boolean;
      };
    };
    setValue: (f: (draft: any) => any) => void;
  };
  toothShade?: { value: any; onChange: (e: any) => void; setValue: (value: any) => void };
  scanMode?: { value: any; onChange: (e: any) => void; setValue: (value: any) => void };
  scanModeListCt?: any[];
  toothShadeList?: IToothShade[];
  isVaildTeethViewer?: boolean;
  onToggleTooth?: (value: number) => void;
  onChangeToothValue?: (value: object | any) => void;
  isProjectCreator: boolean;
}

function TeethViewer({
  isMultidie,
  isViewer,
  numbering,
  teeth,
  tooth,
  copyData,
  bridge,
  toothSvgRef,
  teethContextActions,
  toothShade,
  toothShadeList,
  scanMode,
  scanModeListCt,
  isVaildTeethViewer,
  onToggleTooth,
  onChangeToothValue,
  isProjectCreator,
}: TeethViewerProps) {
  return (
    <StyledTeethViewer dim={!isVaildTeethViewer}>
      <div className="projectIndication__dim">
        <div className="projectIndication__dim_box">
          <CustomText fontSize={16} fontWeight={400} fontColor="#888">
            <T>TEETH_VALIDATION_TEXT</T>
          </CustomText>
        </div>
      </div>

      {!isViewer && !isMultidie ? (
        !!isProjectCreator ? (
          <ButtonGroup className="projectIndication__numbering_tab_box">
            <MuiButton
              className={cx('projectIndication__numbering_tab sm', {
                active: numbering.value === 0,
              })}
              data-type="radio"
              onClick={() => numbering.setValue(0)}
            >
              <T>PROJECT_FDI</T>
            </MuiButton>
            <MuiButton
              className={cx('projectIndication__numbering_tab sm', {
                active: numbering.value === 1,
              })}
              data-type="radio"
              onClick={() => numbering.setValue(1)}
            >
              <T>PROJECT_UNS</T>
            </MuiButton>
          </ButtonGroup>
        ) : (
          <div className="projectIndication__numbering_tab_box syncedUser">
            <MuiButton
              disabled={true}
              className={cx('projectIndication__numbering_tab sm active', {})}
            >
              {numbering.value === 0 && <T>PROJECT_FDI</T>}
              {numbering.value === 1 && <T>PROJECT_UNS</T>}
            </MuiButton>
          </div>
        )
      ) : (
        <div className="projectIndication__numbering_tab_box temp_box"></div>
      )}
      <div className="projectIndication__teeth_box">
        {useMemo(() => {
          return (
            <div
              className={cx('projectIndication__teeth_item', {
                multidie: !!isMultidie,
                indication: !isMultidie,
              })}
            >
              <input className="projectIndication__teeth_focus_point" ref={toothSvgRef} />
              {isViewer ? (
                !!teeth.value &&
                (!!isMultidie ? (
                  <MultidiePlate isEdit={false} tooth={tooth} teeth={teeth} />
                ) : (
                  <TeethSvgV2
                    isEdit={false}
                    tooth={tooth}
                    numbering={{ value: numbering.value }}
                    teeth={{ value: teeth.value }}
                    bridge={{ value: bridge }}
                  />
                ))
              ) : !!isMultidie ? (
                <TeethContextMenu
                  component={
                    <MultidiePlate
                      isEdit={true}
                      tooth={tooth}
                      teeth={teeth}
                      copyData={copyData}
                      teethContextActions={teethContextActions}
                      onToggleTooth={onToggleTooth}
                    />
                  }
                  numbering={numbering}
                  teeth={teeth}
                  tooth={tooth}
                  copyData={copyData}
                  teethContextActions={teethContextActions}
                  onChangeToothValue={onChangeToothValue}
                />
              ) : (
                <TeethContextMenu
                  component={
                    <TeethSvgV2
                      isEdit={true}
                      numbering={numbering}
                      teeth={teeth}
                      tooth={tooth}
                      bridge={bridge}
                      copyData={copyData}
                      teethContextActions={teethContextActions}
                      onToggleTooth={onToggleTooth}
                      onChangeToothValue={onChangeToothValue}
                    />
                  }
                  numbering={numbering}
                  teeth={teeth}
                  tooth={tooth}
                  copyData={copyData}
                  teethContextActions={teethContextActions}
                  onChangeToothValue={onChangeToothValue}
                />
              )}
            </div>
          );
        }, [numbering, teeth.value, tooth.value, bridge, copyData, teethContextActions])}
      </div>
      <div
        className={cx('projectIndication__option_box', {
          isViewer: isViewer,
        })}
      >
        <div className={cx('option_item', {})}>
          <CustomText
            fontSize={14}
            fontWeight={isViewer ? 400 : 500}
            fontColor="#444"
            className={cx('option_title', { isViewer: isViewer })}
          >
            <T>PROJECT_TOOTH_SHADE</T>
          </CustomText>
          {isViewer ? (
            !!toothShade?.value && (
              <CustomText fontSize={14} marginLeft={10} fontWeight={400} fontColor="#444">
                {toothShade?.value?.code}
              </CustomText>
            )
          ) : (
            <MuiWrapper className="projectIndication__tooth_shade_box sm xs-radius" isGlobalStyle>
              <FormControl fullWidth variant="outlined">
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    marginThreshold: 10,
                  }}
                  displayEmpty
                  name="toothShade"
                  value={toothShade?.value}
                  onChange={(e: any) => {
                    toothShade?.setValue(e.target.value);
                  }}
                >
                  {!!toothShadeList &&
                    toothShadeList?.length > 0 &&
                    toothShadeList.map((item: IToothShade, index: number) => {
                      if (!item?.group) {
                        return (
                          <MenuItem key={index} value={item.seq}>
                            {item?.name}
                          </MenuItem>
                        );
                      }
                    })}
                </Select>
              </FormControl>
            </MuiWrapper>
          )}
        </div>
        <div className={cx('option_item', {})}>
          <CustomText
            fontSize={14}
            fontWeight={isViewer ? 400 : 500}
            fontColor="#444"
            className={cx('option_title', { isViewer: isViewer })}
          >
            <T>PROJECT_SCAN_MODE</T>
          </CustomText>
          {isViewer ? (
            !!scanMode?.value && (
              <CustomText
                fontSize={14}
                marginLeft={10}
                fontWeight={isViewer ? 400 : 500}
                fontColor="#444"
              >
                {scanMode?.value?.scanmode}
              </CustomText>
            )
          ) : (
            <MuiWrapper className="projectIndication__tooth_shade_box sm" isGlobalStyle>
              <FormControl fullWidth variant="outlined">
                <Select
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    marginThreshold: 10,
                  }}
                  displayEmpty
                  name="scanMode"
                  value={scanMode?.value}
                  onChange={scanMode?.onChange}
                >
                  {!!scanModeListCt &&
                    scanModeListCt?.length > 0 &&
                    scanModeListCt.map((item: any, index: number) => {
                      const maillaOrMandible = [1, 14];

                      return (
                        <MenuItem key={item.idx} value={item.idx}>
                          {item.scanmode}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </MuiWrapper>
          )}
        </div>
      </div>
    </StyledTeethViewer>
  );
}

const StyledTeethViewer = styled.div<{ dim: boolean }>`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  .projectIndication__dim {
    display: ${({ dim }) => (dim ? 'flex' : 'none')};
    z-index: 1000;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(256, 256, 256, 0.9);
    align-items: center;
    justify-content: center;
    .projectIndication__dim_box {
      display: flex;
      width: 260px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      border-radius: 5px;
      border: 1px solid #e1e1e7;
      background: #f9f9fb;
      margin-bottom: 55px;
      text-align: center;
    }
  }

  .projectIndication__numbering_tab_box {
    position: relative;
    /* margin-right: 20px;
            width: 200px; */
    width: 100%;

    &.syncedUser {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .projectIndication__numbering_tab {
      min-width: 50%;
    }
  }
  .projectIndication__teeth_box {
    /* position: relative; */
    display: flex;
    /* margin: 38px 0; */
    justify-content: center;
    align-items: center;
    /* margin: 20px 50px */
    /* margin-left: auto;
    margin-right: auto; */
    /* padding: 0 100px; */
    width: 100%;
    .projectIndication__teeth_item {
      &.indication {
        width: 292px;
      }

      &.multidie {
        width: 100%;
      }
      .projectIndication__teeth_focus_point {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
        width: 0;
        display: block;
        margin: 0;
        padding: 0;
        border: none;
      }
      .projectIndication__selected_number_box {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: #fff;
        width: 80px;
        height: 80px;
        border: 1px solid transparent;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        .projectIndication__selected_number_in_box {
        }
        .projectIndication__selected_number_text {
          font-size: 13px;
        }
        .projectIndication__selected_number {
          margin-top: 12px;
          font-size: 26px;
        }
        .projectIndication__copied_number {
          position: absolute;
          top: -20px;
          font-size: 12px;
          color: #fff;
        }
      }
    }
  }
  .projectIndication__option_box {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    width: 100%;
    height: 74px;
    .option_item {
      display: flex;
      align-items: center;
      column-gap: 10px;
      .option_title {
        width: 200px;
        &.isViewer {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 140px;
          height: 22px;
          background-color: #f9f9fb;
        }
      }
      .projectIndication__tooth_shade_box {
        /* width: 200px; */
        /* width: 370px; */
        &.syncUser {
          width: 300px !important;
        }
      }
    }
    &.isViewer {
      border-radius: 3px;
      border: 1px solid #e1e1e7;
      padding: 10px;
    }
  }
`;

export default TeethViewer;
